import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import Actions from '../../../store/actions';
import Store from '../../../store';
import { SectionPageValues } from './types';

type DetailResult<T> = { ok: true; data: T } | { ok: false; message: string };
type SaveResult = { ok: true; message: string } | { ok: false; message: string };

export type SectionMappingDialogOptions<TDetail, TMode extends string = string> = {
  documentId: number;
  mode: TMode;
  onClose: () => void;
  onSaved: () => void;
  /* eslint-disable no-unused-vars -- parameter names in function-type signatures are type-only */
  onBusyChange?: (busy: boolean) => void;

  /** Fetch raw detail from the API. */
  fetchDetail: (documentId: number) => Promise<any>;
  /** Normalize the raw response into a typed result. */
  resolveDetail: (response: any) => DetailResult<TDetail>;
  /** Send the save payload to the API. */
  saveDetail: (documentId: number, payload: any) => Promise<any>;
  /** Normalize the raw save response into a typed result. */
  resolveSave: (response: any) => SaveResult;

  /**
   * Extract the correct section-page values from the fetched detail.
   * Responsible for choosing the right map (e.g. section vs original) and
   * merging it onto catalog defaults.
   */
  getInitialPages: (detail: TDetail, mode: TMode) => SectionPageValues;
  /** Extract and normalise the QC status string from the fetched detail. */
  getQcStatus: (detail: TDetail) => string;
  /** Return the PDF URL to display for the given mode, or null if unavailable. */
  getPdfUrl: (detail: TDetail, mode: TMode) => string | null;
  /**
   * Build the API save payload from form state.
   * Receives the full detail object so source-specific fields (e.g. s3_path)
   * can be included without exposing them to the hook's generic logic.
   */
  buildPayload: (
    mode: TMode,
    pages: SectionPageValues,
    qcStatus: string,
    curatorName: string,
    curatorEmail: string,
    detail: TDetail | null
  ) => any;
  /* eslint-enable no-unused-vars */
};

const useSectionMappingDialog = <TDetail, TMode extends string = string>({
  documentId,
  mode,
  onClose,
  onSaved,
  onBusyChange,
  fetchDetail,
  resolveDetail,
  saveDetail,
  resolveSave,
  getInitialPages,
  getQcStatus,
  getPdfUrl,
  buildPayload
}: SectionMappingDialogOptions<TDetail, TMode>) => {
  const { dispatch } = useContext<any>(Store);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<TDetail | null>(null);
  const [sectionPages, setSectionPages] = useState<SectionPageValues>({});
  const [qcStatus, setQcStatus] = useState('to_qc');
  const isMountedRef = useRef(true);

  const busy = loading || saving;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      onBusyChange?.(false);
    };
  }, [onBusyChange]);

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const handleClose = useCallback(() => {
    if (!busy) {
      onClose();
    }
  }, [busy, onClose]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetchDetail(documentId);
        const result = resolveDetail(response);

        if (!result.ok) {
          await dispatch({
            type: Actions.SET_ALERT,
            value: { status: true, message: result.message }
          });
          onClose();
          return;
        }

        if (!isMountedRef.current) return;

        setDetail(result.data);
        setQcStatus(getQcStatus(result.data));
        setSectionPages(getInitialPages(result.data, mode));
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to load detail';
        await dispatch({ type: Actions.SET_ALERT, value: { status: true, message } });
        onClose();
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    };

    load();
  }, [
    documentId,
    dispatch,
    fetchDetail,
    getInitialPages,
    getQcStatus,
    mode,
    onClose,
    resolveDetail
  ]);

  const pdfUrl = detail ? getPdfUrl(detail, mode) : null;

  const handlePageChange = useCallback((key: string, value: string) => {
    setSectionPages(current => ({ ...current, [key]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!qcStatus.trim()) {
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'QC status is required' }
      });
      return;
    }

    setSaving(true);
    try {
      let curatorName = '';
      let curatorEmail = '';
      try {
        const user = await Auth.currentUserInfo();
        curatorName = user.attributes['custom:user'] ?? '';
        curatorEmail = user.attributes.email ?? '';
      } catch {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Unable to load curator details' }
        });
        return;
      }

      if (!curatorName || !curatorEmail) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Curator name and email are required' }
        });
        return;
      }

      const payload = buildPayload(mode, sectionPages, qcStatus, curatorName, curatorEmail, detail);
      const response = await saveDetail(documentId, payload);
      const result = resolveSave(response);

      if (!result.ok) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: result.message }
        });
        return;
      }

      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: result.message, color: 'success' }
      });
      onSaved();
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save';
      await dispatch({ type: Actions.SET_ALERT, value: { status: true, message } });
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [
    buildPayload,
    detail,
    dispatch,
    documentId,
    mode,
    onClose,
    onSaved,
    qcStatus,
    resolveSave,
    saveDetail,
    sectionPages
  ]);

  return {
    loading,
    saving,
    busy,
    sectionPages,
    qcStatus,
    setQcStatus,
    pdfUrl,
    handleClose,
    handlePageChange,
    handleSave
  };
};

export default useSectionMappingDialog;
